import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Steps,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd";
import { InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { LayersControl, MapContainer, Polygon, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { apiClient } from "../../../services/api/apiClient";
import { parseKMLFile } from "../../producers/utils/kml-parser";
import { computeGeodesicAreaHa } from "../../../utils/geo";

const { Dragger } = Upload;
const { Text } = Typography;

interface CatalogueOption {
  id: number;
  name: string;
  code?: string;
  is_active?: boolean;
}

interface Props {
  open: boolean;
  farmId: number;
  farmName: string;
  lastFinalizedAudit: {
    id: number;
    reference: string | null;
    season_year: number | null;
  } | null;
  onClose: () => void;
  onCreated: () => void;
}

const currentYear = new Date().getFullYear();

const geometryBounds = (geom: GeoJSON.Geometry): [[number, number], [number, number]] | null => {
  const coords: number[][] = [];
  if (geom.type === "Polygon") {
    for (const ring of geom.coordinates as number[][][]) coords.push(...ring);
  } else if (geom.type === "MultiPolygon") {
    for (const poly of geom.coordinates as number[][][][]) {
      for (const ring of poly) coords.push(...ring);
    }
  } else {
    return null;
  }
  if (coords.length === 0) return null;
  let minLat = Infinity,
    maxLat = -Infinity,
    minLon = Infinity,
    maxLon = -Infinity;
  for (const [lon, lat] of coords) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  return [
    [minLat, minLon],
    [maxLat, maxLon],
  ];
};

const geometryToLeafletPositions = (
  geom: GeoJSON.Geometry,
): [number, number][][][] => {
  const polys: [number, number][][][] = [];
  if (geom.type === "Polygon") {
    polys.push(
      (geom.coordinates as number[][][]).map((ring) =>
        ring.map(([lon, lat]) => [lat, lon] as [number, number]),
      ),
    );
  } else if (geom.type === "MultiPolygon") {
    for (const poly of geom.coordinates as number[][][][]) {
      polys.push(
        poly.map((ring) =>
          ring.map(([lon, lat]) => [lat, lon] as [number, number]),
        ),
      );
    }
  }
  return polys;
};

export const CreateCanonicalPlotModal = ({
  open,
  farmId,
  farmName,
  lastFinalizedAudit,
  onClose,
  onCreated,
}: Props) => {
  const { t } = useTranslation();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [geom, setGeom] = useState<GeoJSON.Geometry | null>(null);
  const [autoSurface, setAutoSurface] = useState<number | null>(null);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const { result: varietiesRes } = useList<CatalogueOption>({
    resource: "varieties",
    pagination: { mode: "off" },
  });
  const { result: patternsRes } = useList<CatalogueOption>({
    resource: "patterns",
    pagination: { pageSize: 200 },
  });
  const { result: plotTypesRes } = useList<CatalogueOption>({
    resource: "plot-types",
    pagination: { pageSize: 200 },
  });

  useEffect(() => {
    if (open) {
      setStep(0);
      setGeom(null);
      setAutoSurface(null);
      setFileList([]);
      form.resetFields();
    }
  }, [open, form]);

  const bounds = useMemo(
    () => (geom ? geometryBounds(geom) : null),
    [geom],
  );
  const positions = useMemo(
    () => (geom ? geometryToLeafletPositions(geom) : []),
    [geom],
  );

  const handleKMLUpload = async (file: File) => {
    setParsing(true);
    try {
      const fc = await parseKMLFile(file);
      if (fc.features.length === 0) {
        message.error(
          t(
            "farms.show.create_plot.kml_no_polygons",
            "El KML no contiene polígonos.",
          ),
        );
        return false;
      }
      const first = fc.features[0];
      if (!first.geometry) return false;
      const g = first.geometry as GeoJSON.Geometry;
      setGeom(g);
      const area = computeGeodesicAreaHa(g);
      const rounded = area != null ? Number(area.toFixed(4)) : null;
      setAutoSurface(rounded);
      // Prefill surface in form so el admin la ve ya rellenada al
      // pasar al paso 2 (puede editarla antes de guardar).
      if (rounded != null) {
        form.setFieldValue("surface", rounded);
      }
      message.success(
        t("farms.show.create_plot.kml_ok", "KML cargado correctamente"),
      );
    } catch (err: any) {
      message.error(
        err?.message ||
          t("farms.show.create_plot.kml_error", "Error al leer KML"),
      );
    } finally {
      setParsing(false);
    }
    return false; // prevent default upload
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!geom) {
        message.error(
          t(
            "farms.show.create_plot.need_geom",
            "Carga un KML antes de guardar.",
          ),
        );
        setStep(0);
        return;
      }
      setSubmitting(true);
      await apiClient.post(`/v1/farms/${farmId}/canonical-plots/`, {
        name: values.name,
        variety_id: values.variety_id,
        pattern_id: values.pattern_id ?? null,
        plot_type_id: values.plot_type_id ?? null,
        plants: values.plants ?? 0,
        crop_date: values.crop_date,
        plantation_frame_row: values.plantation_frame_row ?? 6.0,
        plantation_frame_column: values.plantation_frame_column ?? 4.0,
        surface: values.surface,
        geom,
      });
      message.success(
        t(
          "farms.show.create_plot.success",
          "Parcela canónica creada correctamente",
        ),
      );
      onCreated();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) {
        // Form validation error — AntD already highlighted the fields.
        return;
      }
      const detail = err?.response?.data?.detail;
      const fieldErrors = err?.response?.data;
      if (detail) {
        message.error(detail);
      } else if (fieldErrors && typeof fieldErrors === "object") {
        const firstMsg = Object.values(fieldErrors)[0];
        message.error(
          typeof firstMsg === "string"
            ? firstMsg
            : t(
                "farms.show.create_plot.error",
                "No se pudo crear la parcela",
              ),
        );
      } else {
        message.error(
          t(
            "farms.show.create_plot.error",
            "No se pudo crear la parcela",
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!lastFinalizedAudit) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title={t(
          "farms.show.create_plot.title",
          "Añadir parcela canónica",
        )}
      >
        <Alert
          type="warning"
          showIcon
          message={t(
            "farms.show.create_plot.no_audit_title",
            "No hay auditorías finalizadas",
          )}
          description={t(
            "farms.show.create_plot.no_audit_desc",
            "La finca necesita al menos una auditoría finalizada antes de poder añadir una parcela canónica retroactiva.",
          )}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={820}
      title={
        <Space>
          <span>
            {t(
              "farms.show.create_plot.title",
              "Añadir parcela canónica",
            )}
          </span>
          <Tag color="blue">{farmName}</Tag>
        </Space>
      }
      footer={
        <Space>
          <Button onClick={onClose}>
            {t("common.cancel", "Cancelar")}
          </Button>
          {step === 0 ? (
            <Button
              type="primary"
              disabled={!geom}
              onClick={() => setStep(1)}
            >
              {t("common.next", "Siguiente")}
            </Button>
          ) : (
            <>
              <Button onClick={() => setStep(0)}>
                {t("common.back", "Atrás")}
              </Button>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleSubmit}
              >
                {t("common.create", "Crear")}
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          {
            title: t(
              "farms.show.create_plot.step_geom",
              "Geometría",
            ),
          },
          {
            title: t(
              "farms.show.create_plot.step_data",
              "Datos de la parcela",
            ),
          },
        ]}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <span>
            {t(
              "farms.show.create_plot.audit_hint",
              "La parcela se asociará a la última auditoría finalizada",
            )}
            : <strong>{lastFinalizedAudit.reference ?? `#${lastFinalizedAudit.id}`}</strong>
            {lastFinalizedAudit.season_year && (
              <>
                {" "}
                ({t("common.season", "campaña")} {lastFinalizedAudit.season_year})
              </>
            )}
          </span>
        }
      />

      {step === 0 && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Dragger
            accept=".kml"
            multiple={false}
            fileList={fileList}
            beforeUpload={handleKMLUpload}
            onRemove={() => {
              setGeom(null);
              setAutoSurface(null);
              setFileList([]);
            }}
            onChange={(info) => setFileList(info.fileList.slice(-1))}
            disabled={parsing}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {t(
                "farms.show.create_plot.drop_kml",
                "Arrastra el KML aquí o haz clic para seleccionarlo",
              )}
            </p>
            <p className="ant-upload-hint">
              {t(
                "farms.show.create_plot.kml_hint",
                "Sólo se aceptan polígonos (Polygon / MultiPolygon)",
              )}
            </p>
          </Dragger>

          {geom && bounds && (
            <>
              <Space wrap>
                <Tag color="green" icon={<UploadOutlined />}>
                  {t(
                    "farms.show.create_plot.geom_loaded",
                    "Geometría cargada",
                  )}
                </Tag>
                {autoSurface != null && (
                  <Text type="secondary">
                    {t("farms.show.create_plot.auto_surface", "Superficie")}:{" "}
                    <strong>{autoSurface.toFixed(4)} ha</strong>
                  </Text>
                )}
              </Space>
              <div style={{ height: 320, width: "100%" }}>
                <MapContainer
                  bounds={bounds}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom
                >
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer
                      checked
                      name={t("map.satellite", "Satélite")}
                    >
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution="&copy; Esri"
                        maxZoom={19}
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer
                      name={t("map.streets", "Calles")}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap contributors"
                        maxZoom={19}
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>
                  {positions.map((poly, idx) => (
                    <Polygon
                      key={idx}
                      positions={poly}
                      pathOptions={{ color: "#ffeb3b", weight: 2 }}
                    />
                  ))}
                </MapContainer>
              </div>
            </>
          )}
        </Space>
      )}

      {step === 1 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            crop_date: currentYear,
            plants: 0,
            plantation_frame_row: 6.0,
            plantation_frame_column: 4.0,
            surface: autoSurface ?? undefined,
          }}
        >
          <Form.Item
            label={t("plot.name", "Nombre")}
            name="name"
            rules={[
              {
                required: true,
                message: t(
                  "farms.show.create_plot.field_required",
                  "Campo obligatorio",
                ),
              },
            ]}
          >
            <Input placeholder={t("plot.name_placeholder", "p. ej. Parcela 3")} />
          </Form.Item>
          <Form.Item
            label={t("plot.variety", "Variedad")}
            name="variety_id"
            rules={[
              {
                required: true,
                message: t(
                  "farms.show.create_plot.field_required",
                  "Campo obligatorio",
                ),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={(varietiesRes?.data ?? []).map((v) => ({
                value: v.id,
                label: v.is_active === false ? `${v.name} (archivada)` : v.name,
              }))}
              placeholder={t(
                "varieties.select_placeholder",
                "Selecciona variedad",
              )}
            />
          </Form.Item>
          <Form.Item
            label={t("plot.pattern", "Patrón")}
            name="pattern_id"
            tooltip={t(
              "farms.show.create_plot.pattern_hint",
              "Opcional. Si no lo indicas, se usa el primero del catálogo.",
            )}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={(patternsRes?.data ?? []).map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              placeholder={t("plot.pattern_placeholder", "Selecciona patrón")}
            />
          </Form.Item>
          <Form.Item
            label={t("plot.plot_type", "Tipo de plantación")}
            name="plot_type_id"
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={(plotTypesRes?.data ?? []).map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              placeholder={t(
                "plot.plot_type_placeholder",
                "Selecciona tipo",
              )}
            />
          </Form.Item>
          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item
              label={t("plot.crop_date", "Año de plantación")}
              name="crop_date"
              rules={[
                {
                  required: true,
                  message: t(
                    "farms.show.create_plot.field_required",
                    "Campo obligatorio",
                  ),
                },
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber
                min={2000}
                max={currentYear + 5}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              label={t("plot.plants", "Nº plantas")}
              name="plants"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item
              label={t("plot.frame_row", "Marco (fila, m)")}
              name="plantation_frame_row"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label={t("plot.frame_column", "Marco (columna, m)")}
              name="plantation_frame_column"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Form.Item
            label={t("plot.surface", "Superficie (ha)")}
            name="surface"
            tooltip={
              autoSurface != null
                ? t(
                    "farms.show.create_plot.surface_auto_hint",
                    "Pre-rellenada desde el KML. Edítala si el polígono incluye pasillos u otras zonas no cultivadas.",
                  )
                : undefined
            }
            rules={[
              {
                required: true,
                message: t(
                  "farms.show.create_plot.field_required",
                  "Campo obligatorio",
                ),
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={4}
              style={{ width: "100%" }}
              placeholder={
                autoSurface != null
                  ? autoSurface.toFixed(4)
                  : t(
                      "farms.show.create_plot.surface_placeholder",
                      "Hectáreas",
                    )
              }
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default CreateCanonicalPlotModal;
